import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { EmailCommunicationSetting } from '@models/communications/email-notifications-settings.model';
import { SmsCommunicationSetting } from '@models/communications/sms-notifications-settings.model';
import { CommunicationsService } from '@services/communications.service';
import { ClinicsService } from '@services/clinics.service';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { GenericDialogComponent } from '../dialogs/generic-confirm/generic-confirm.component';

@Component({
  selector: 'app-communications',
  templateUrl: './communications.component.html',
  styleUrls: ['./communications.component.less'],
})
export class CommunicationsComponent implements OnInit, OnDestroy {
  disableGrid: boolean;
  loading: boolean;
  confirmScheduledSettings: EmailCommunicationSetting;
  confirmChangedSettings: EmailCommunicationSetting;
  confirmCancelledSettings: EmailCommunicationSetting;
  visitEmailSettings: EmailCommunicationSetting;
  visitSmsSettings: SmsCommunicationSetting;
  formEmailSettings: EmailCommunicationSetting;
  formSmsSettings: SmsCommunicationSetting;
  ccEmailSettings: EmailCommunicationSetting;
  ccSmsSettings: SmsCommunicationSetting;
  private unsub: Subject<void> = new Subject<void>();

  constructor(
    private router: Router,
    private communicationsService: CommunicationsService,
    private viewEmailTemplateDialog: MatDialog,
    public clinicsService: ClinicsService
  ) {}

  ngOnInit() {
    this.getCommunicationsSettings();
    this.detectChanges();
  }

  getCommunicationsSettings() {
    this.loading = true;
    forkJoin([
      this.communicationsService.getEmailNotificationsSettings(),
      this.communicationsService.getSmsNotificationsSettings(),
    ]).subscribe(([emailSettings, smsSettings]) => {
      this.visitEmailSettings = emailSettings.visitReminder;
      this.formEmailSettings = emailSettings.formsReminder;
      this.ccEmailSettings = emailSettings.creditCardReminder;
      this.confirmScheduledSettings = emailSettings.scheduledConfirmation;
      this.confirmChangedSettings = emailSettings.changedConfirmation;
      this.confirmCancelledSettings = emailSettings.cancelledConfirmation;
      this.visitSmsSettings = smsSettings.visitReminder;
      this.formSmsSettings = smsSettings.formsReminder;
      this.ccSmsSettings = smsSettings.creditCardReminder;

      if (this.visitSmsSettings?.message) {
        this.visitSmsSettings.messageWithLineBreaks = this.visitSmsSettings.message.split('\n').join('<br>');
      }
      if (this.formSmsSettings?.message) {
        this.formSmsSettings.messageWithLineBreaks = this.formSmsSettings.message.split('\n').join('<br>');
      }
      if (this.ccSmsSettings?.message) {
        this.ccSmsSettings.messageWithLineBreaks = this.ccSmsSettings.message.split('\n').join('<br>');
      }
      this.loading = false;
    });
  }

  detectChanges() {
    this.communicationsService.communicationsSettingsUpdated.pipe(takeUntil(this.unsub)).subscribe(() => {
      this.disableGrid = false;
      if (this.communicationsService.refreshRequired) {
        this.communicationsService.refreshRequired = false;
        this.getCommunicationsSettings();
      }
    });
  }

  updateEmailNotification(emailSettings: EmailCommunicationSetting) {
    this.communicationsService.updateEmailNotificationsSettings(emailSettings).subscribe();
  }

  updateSmsNotification(smsSettings: SmsCommunicationSetting) {
    this.communicationsService.updateSmsNotificationsSettings(smsSettings).subscribe();
  }

  navigateToEditEmailReminderPanel(setting: EmailCommunicationSetting) {
    this.disableGrid = true;
    this.router.navigate([
      '/management/communication/appointments',
      { outlets: { 'action-panel-secondary': ['edit-email-reminder', setting.id] } },
    ]);
  }

  navigateToEditSmsReminderPanel() {
    this.disableGrid = true;
    this.router.navigate([
      '/management/communication/appointments',
      { outlets: { 'action-panel': ['edit-visit-reminder-settings', 'sms'] } },
    ]);
  }

  navigateToSendTestVisitReminderPanel(type: string) {
    this.disableGrid = true;

    this.router.navigate([
      '/management/communication/appointments',
      { outlets: { 'action-panel': ['send-test-visit-reminder', type] } },
    ]);
  }

  viewEmailTemplate(emailSetting: EmailCommunicationSetting) {
    const dialogRef = this.viewEmailTemplateDialog.open(GenericDialogComponent, {
      width: '560px',
      data: {
        content: emailSetting.emailHtml,
        showCancel: false,
        confirmButtonText: 'Ok',
      },
    });
  }

  ngOnDestroy() {
    this.unsub.next();
    this.unsub.complete();
  }
}
